import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import QR from 'react-qr-code';
import QrScanner from 'qr-scanner';
import log from '../utils/logger';
import { attachAndroidEvent, checkCameraPermission, removeAndroidEvent } from '../utils/events';
import Alert, { ALERT_TYPE } from './Alert';
import { useTranslation } from 'react-i18next';

const QRCode = ({ value, className = '' }) => {
  return (
    <div className={`qr-code ${className}`} aria-label="qrcode">
      <QR style={{ height: "auto", maxWidth: "100%", width: "100%" }} value={value} />
    </div>
  );
};

QRCode.propTypes = {
  className: PropTypes.string,
  value: PropTypes.string
};

const Scanner = ({ className = '', onResult }) => {
  const {t} = useTranslation(['common']);

  const [qrScanner, setQrScanner] = useState(null);
  const [data, setData] = useState(null);
  const [started, setStarted] = useState(false);
  const [error, setError] = useState('');
  const cameraRef = useRef();
  const qrScannerRef = useRef();

  const updateFlashAvailability = (scanner) => {
    scanner && scanner.hasFlash().then(hasFlash => {
      log.debug('FLASH', hasFlash);
    });
  };

    const destroyScanner = useCallback(() => {
        if (qrScannerRef.current) {
            qrScannerRef.current.stop();
            qrScannerRef.current.destroy();
            qrScannerRef.current = null;
        }
        setQrScanner(null);
        setStarted(false);
    }, []);

  const startQRScanner = (scanner) => {
    if (!started) {
      scanner = scanner || qrScanner;
      try {
        scanner && scanner.start().then(() => {
          setStarted(true);
          updateFlashAvailability(scanner);
          setError(null);
        }).catch((e) => {
          log.error(e);
          setStarted(false);
          setError(typeof e === 'object' ? e.message : (e === 'Camera not found.' ? t('common:error.camera_not_found') : e));
        });
      } catch(e) {}
    }
  };

  const loadCamera = useCallback(() => {
    if (cameraRef.current && !qrScanner) {
      const scanner = new QrScanner(
        cameraRef.current,
        (result) => {
          if (result.data && data !== result.data) {
            setData(result.data);
            scanner.stop();

            setStarted(false);

            onResult && onResult(result.data, null, scanner);
          }
        },
        {
          preferredCamera: QrScanner.DeviceId, //QrScanner.FacingMode | QrScanner.DeviceId,
          onDecodeError: (e) => {
            // setError(typeof e === 'object' ? e.message : e);
          },
          returnDetailedScanResult: true,
        }
      );

      setQrScanner(scanner);
      qrScannerRef.current = scanner;
      startQRScanner(scanner);
    }
  }, [cameraRef.current]);

  useEffect(() => {
    if (cameraRef.current) {
      attachAndroidEvent('camera', (e) => {
        if (e.detail) {
          loadCamera();
        } else {
          setError(t('common:error.camera_not_found'));
        }
      });

      checkCameraPermission().catch((e) => {
        loadCamera();
      });
    }
  }, [cameraRef.current]);

    useEffect(() => {
        attachAndroidEvent('camera_start', (e) => {
            if (qrScannerRef.current) {
                if (e.detail) {
                    startQRScanner(qrScannerRef.current);
                } else {
                    destroyScanner();
                }
            }
        });

        return () => {
            destroyScanner();
            removeAndroidEvent('camera');
            removeAndroidEvent('camera_start');
        };
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden || document.visibilityState === 'hidden') {
                destroyScanner();
            } else if (cameraRef.current && qrScannerRef.current) {
                startQRScanner(qrScannerRef.current);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    return (
    <section className={className}>
      <div className="qr-reader">
        {error && (
          <Alert type={ALERT_TYPE.ERROR}>{error}</Alert>
        )}
        <video ref={cameraRef} onClick={() => startQRScanner(qrScanner)}></video>
        <div className="scan-region-highlight">
          <svg className="scan-region-highlight-svg" viewBox="0 0 238 238" preserveAspectRatio="none">
            <path d="M31 2H10a8 8 0 0 0-8 8v21M207 2h21a8 8 0 0 1 8 8v21m0 176v21a8 8 0 0 1-8 8h-21m-176 0H10a8 8 0 0 1-8-8v-21"></path>
          </svg>
          <svg className="code-outline-highlight" preserveAspectRatio="none">
            <polygon></polygon>
          </svg>
        </div>
      </div>
    </section>
  )
};

Scanner.prototype = {
  className: PropTypes.string,
  onResult: PropTypes.func
};

QRCode.Scanner = Scanner;

export default QRCode;
